import { Component, h, Prop, Watch, Element, Host, State } from '@stencil/core';
import { isMagnoliaEditingMode } from '../../helper';
import { observereGroupedElementsToReadjust } from '../../global/utility/observe-grouped-elements-to-readjust/observe-grouped-elements-to-readjust';
import { readjustGroupedElements } from '../../global/utility/readjust-grouped-elements/readjust-grouped-elements';
import { TeaserAspectRatio } from '../../types/global';

interface UiCmsFullScreenTeaserGroupBody {
  columns?: number;
  distribution: '6_6' | '8_4' | '4_8';
  aspectRatio?: TeaserAspectRatio;
}

@Component({
  tag: 'ui-cms-fullscreen-teaser-group',
  styleUrls: {
    default: 'ui-cms-fullscreen-teaser-group.scss',
    editing: 'ui-cms-fullscreen-teaser-group.magnolia.scss',
  },
})
export class UiCmsFullscreenTeaserGroup {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) body: UiCmsFullScreenTeaserGroupBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @State()
  teasers: any;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
  }

  private calculateAspectRatio(largeTeaser, smallTeaser, largeTeaserAspectRatio): string {
    const [widthRatio, heightRatio] = largeTeaserAspectRatio.split(' / ').map((val) => Number(val));
    const largeTeaserMediaWidth = largeTeaser?.querySelector('sd-teaser-media').getBoundingClientRect().width;
    const largeTeaserMediaHeight = largeTeaserMediaWidth * (heightRatio / widthRatio);
    const smallTeaserMediaWidth = smallTeaser?.querySelector('sd-teaser-media').getBoundingClientRect().width;
    const smallTeaserAspectRatio = smallTeaserMediaWidth / largeTeaserMediaHeight;

    return `${smallTeaserAspectRatio * 10} / 10`;
  }

  private setFormatForTeasers() {
    this.teasers.forEach(async (teaser: HTMLUiCmsTeaserArticleElement, index: number) => {
      let aspectRatio: TeaserAspectRatio | string = this.body.aspectRatio?.split('_').join(' / ') || '16 / 9';

      if (this.body.columns === 2) {
        const isEven = (index + 1) % 2 === 0;

        if (this.body.distribution === '8_4' && isEven) {
          aspectRatio = this.calculateAspectRatio(this.teasers[index - 1], teaser, aspectRatio);
        }

        if (this.body.distribution === '4_8' && !isEven) {
          aspectRatio = this.calculateAspectRatio(this.teasers[index + 1], teaser, aspectRatio);
        }
      }

      teaser.setAttribute('aspect-ratio', aspectRatio);
    });
  }

  componentDidLoad() {
    this.teasers = [].slice.call(this.el.querySelectorAll('ui-cms-fullscreen-teaser'));

    if (isMagnoliaEditingMode()) {
      const mglBars = [].slice.call(this.el.querySelectorAll('.mgnlEditorBar.component'));
      readjustGroupedElements(mglBars, this.teasers);
      observereGroupedElementsToReadjust(this.el, this.teasers);
    }

    this.setFormatForTeasers();
  }

  render() {
    return (
      <Host>
        <div
          class={{
            'fullscreen-teaser-group': true,
            [`fullscreen-teaser-group--${this.body.columns || '1'}`]: true,
            [`column-distribution--${this.body.distribution || '6_6'}`]: this.body.columns === 2,
          }}
        >
          <slot></slot>
        </div>
      </Host>
    );
  }
}
